import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageHeader, StatCard, Card, Btn, Input, Select, Modal, Empty, Spinner, Badge, useToast } from '../../components/shared/UI';
import api from '../../utils/api';

const CATEGORY_COLORS = {
  auth: '#2E75B6', profile: '#0EA5E9', document: '#10B981', client: '#F59E0B',
  item: '#8B5CF6', company: '#14B8A6', billing: '#EC4899', admin: '#EF4444',
  backup: '#6366F1', other: '#64748b',
};
const catColor = c => CATEGORY_COLORS[c] || '#64748b';

const CATEGORY_ICONS = {
  auth: '🔑', profile: '👤', document: '📄', client: '🤝', item: '📦',
  company: '🏢', billing: '💳', admin: '🛡', backup: '💾', other: '•',
};

const PAGE_SIZES = [25, 50, 100, 200];

const fmtDateTime = d => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const fmtAgo = d => {
  if (!d) return '';
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const EMPTY_FILTERS = { q: '', user: '', category: '', action: '', success: '', from: '', to: '' };

export default function ActivityLog() {
  const [data, setData]       = useState({ entries: [], total: 0, pages: 1, page: 1 });
  const [facets, setFacets]   = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage]       = useState(1);
  const [limit, setLimit]     = useState(50);
  const [loading, setLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);
  const [auto, setAuto]       = useState(false);
  const [detail, setDetail]   = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeDays, setPurgeDays] = useState('90');
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState('');
  const { show, ToastContainer } = useToast();

  // Debounce the free-text box so typing doesn't hammer the API.
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q), 350);
    return () => clearTimeout(t);
  }, [filters.q]);

  const query = useMemo(() => {
    const p = { page, limit };
    const active = { ...filters, q: debouncedQ };
    Object.entries(active).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters, debouncedQ, page, limit]);

  // Load errors go to a banner rather than a toast: `show` is a fresh function
  // on every render, so depending on it here would re-fire the effect forever.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/admin/activity', { params: query });
      setData(res);
      setErr('');
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to load activity');
    } finally {
      setLoading(false);
      setFirstLoad(false);
    }
  }, [query]);

  const loadFacets = useCallback(async () => {
    try {
      const { data: res } = await api.get('/admin/activity/facets');
      setFacets(res);
    } catch { /* non-fatal — filters just stay empty */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFacets(); }, [loadFacets]);

  // Auto-refresh
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (!auto) return undefined;
    const id = setInterval(() => { loadRef.current(); }, 10000);
    return () => clearInterval(id);
  }, [auto]);

  const set = k => e => { setPage(1); setFilters(f => ({ ...f, [k]: e.target.value })); };
  const reset = () => { setFilters(EMPTY_FILTERS); setPage(1); };
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const exportCsv = async () => {
    setBusy(true);
    try {
      const params = { ...query };
      delete params.page; delete params.limit;
      const res = await api.get('/admin/activity/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      show('CSV exported');
    } catch (e) {
      show('Export failed', 'error');
    } finally { setBusy(false); }
  };

  const purge = async () => {
    const days = parseInt(purgeDays, 10);
    const msg = days > 0
      ? `Permanently delete all activity entries older than ${days} days?`
      : 'Permanently delete the ENTIRE activity log?';
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const { data: res } = await api.delete('/admin/activity', { params: { days: days > 0 ? days : 0 } });
      show(`${res.deleted} entrie(s) deleted`);
      setPurgeOpen(false);
      setPage(1);
      load(); loadFacets();
    } catch (e) {
      show(e.response?.data?.message || 'Purge failed', 'error');
    } finally { setBusy(false); }
  };

  if (firstLoad) return <Spinner />;

  const t = facets?.totals || {};
  const maxDay = Math.max(1, ...(facets?.byDay || []).map(d => d.count));

  return (
    <div>
      <ToastContainer />
      <PageHeader
        title="Activity Log"
        subtitle="Every action taken by every user in the system"
        actions={
          <>
            <Btn variant={auto ? 'success' : 'outline'} onClick={() => setAuto(a => !a)}>
              {auto ? '● Live (10s)' : '○ Live off'}
            </Btn>
            <Btn variant="outline" onClick={() => { load(); loadFacets(); }} disabled={loading}>↻ Refresh</Btn>
            <Btn variant="outline" onClick={exportCsv} disabled={busy}>⬇ Export CSV</Btn>
            <Btn variant="danger" onClick={() => setPurgeOpen(true)}>🗑 Purge</Btn>
          </>
        }
      />

      {err && (
        <Card style={{ marginBottom: 16, borderColor: 'var(--danger)' }}>
          <p style={{ color: 'var(--danger)', fontSize: 14 }}>{err}</p>
        </Card>
      )}

      {/* Headline counters */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 20 }}>
        <StatCard label="Total events"     value={(t.total ?? 0).toLocaleString()}     icon="📜" color="#2E75B6" />
        <StatCard label="Last 24 hours"    value={(t.last24h ?? 0).toLocaleString()}   icon="⏱"  color="#10B981" />
        <StatCard label="Last 7 days"      value={(t.last7d ?? 0).toLocaleString()}    icon="📆" color="#8B5CF6" />
        <StatCard label="Failures (24h)"   value={(t.failures24h ?? 0).toLocaleString()} icon="⚠"  color="#EF4444"
                  sub={t.failures24h ? 'Needs a look' : 'All clear'} />
        <StatCard label="Users tracked" value={(facets?.users?.length ?? 0).toLocaleString()} icon="👥" color="#F59E0B" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 16, marginBottom: 20 }}>
        {/* 30-day volume sparkline */}
        {facets?.byDay?.length > 0 && (
          <Card>
            <h3 style={cardH}>Events — last 30 days</h3>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 90 }}>
              {facets.byDay.map(d => (
                <div key={d.date} title={`${d.date}: ${d.count} events`}
                  style={{
                    flex: 1, minWidth: 4, borderRadius: '3px 3px 0 0',
                    height: `${Math.max(4, (d.count / maxDay) * 100)}%`,
                    background: 'linear-gradient(180deg, #2E75B6, #1A3C6E)',
                  }} />
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-subtle)', marginTop: 6 }}>
              <span>{facets.byDay[0]?.date}</span>
              <span>{facets.byDay[facets.byDay.length - 1]?.date}</span>
            </div>
          </Card>
        )}

        {/* Busiest accounts */}
        <Card>
          <h3 style={cardH}>Most active users (30 days)</h3>
          {!facets?.topUsers?.length
            ? <p style={{ fontSize: 13, color: 'var(--text-subtle)' }}>No activity in the last 30 days</p>
            : facets.topUsers.map(u => (
                <div key={u.id}
                  onClick={() => { setFilters({ ...EMPTY_FILTERS, user: u.id }); setPage(1); }}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--border-light)', cursor: 'pointer' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name || '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{u.email}</div>
                  </div>
                  <Badge label={`${u.count} events`} color="#2E75B6" />
                </div>
              ))
          }
        </Card>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
          <Input label="Search" placeholder="user, description, IP…" value={filters.q} onChange={set('q')} />
          <Select label="User" value={filters.user} onChange={set('user')}>
            <option value="">All users</option>
            {(facets?.users || []).map(u => (
              <option key={u.id} value={u.id}>{u.name || u.email || u.id} ({u.count})</option>
            ))}
          </Select>
          <Select label="Category" value={filters.category} onChange={set('category')}>
            <option value="">All categories</option>
            {(facets?.categories || []).map(c => (
              <option key={c.category} value={c.category}>{c.category} ({c.count})</option>
            ))}
          </Select>
          <Select label="Action" value={filters.action} onChange={set('action')}>
            <option value="">All actions</option>
            {(facets?.actions || []).map(a => (
              <option key={a.action} value={a.action}>{a.action} ({a.count})</option>
            ))}
          </Select>
          <Select label="Result" value={filters.success} onChange={set('success')}>
            <option value="">All results</option>
            <option value="true">Success only</option>
            <option value="false">Failures only</option>
          </Select>
          <Input label="From" type="date" value={filters.from} onChange={set('from')} />
          <Input label="To"   type="date" value={filters.to}   onChange={set('to')} />
          <Select label="Per page" value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}>
            {PAGE_SIZES.map(n => <option key={n} value={n}>{n} rows</option>)}
          </Select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {data.total.toLocaleString()} matching entrie(s)
            {activeFilterCount > 0 && ` · ${activeFilterCount} filter(s) active`}
          </span>
          {activeFilterCount > 0 && <Btn size="sm" variant="ghost" onClick={reset}>Clear filters</Btn>}
        </div>
      </Card>

      {/* Table */}
      {data.entries.length === 0
        ? <Empty icon="📜" message={activeFilterCount ? 'No entries match these filters' : 'No activity recorded yet'} />
        : (
          <Card style={{ padding: 0, overflow: 'hidden', opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 980, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                    <th style={th}>When</th>
                    <th style={th}>User</th>
                    <th style={th}>Action</th>
                    <th style={th}>Description</th>
                    <th style={th}>Result</th>
                    <th style={th}>IP</th>
                    <th style={{ ...th, textAlign: 'right' }}>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e, i) => (
                    <tr key={e._id}
                      onClick={() => setDetail(e)}
                      style={{
                        borderBottom: '1px solid var(--border-light)',
                        background: i % 2 === 0 ? 'var(--card-bg)' : 'var(--hover-bg)',
                        cursor: 'pointer',
                      }}>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <div style={{ fontSize: 13, color: 'var(--text)' }}>{fmtDateTime(e.createdAt)}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{fmtAgo(e.createdAt)}</div>
                      </td>
                      <td style={td}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{e.userName || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{e.userEmail || 'anonymous'}</div>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <Badge label={`${CATEGORY_ICONS[e.category] || '•'} ${e.action}`} color={catColor(e.category)} />
                      </td>
                      <td style={{ ...td, maxWidth: 420 }}>
                        <div style={{ fontSize: 13 }}>{e.description}</div>
                        {e.message && <div style={{ fontSize: 11, color: 'var(--danger)' }}>{e.message}</div>}
                      </td>
                      <td style={td}>
                        <Badge label={e.success ? `${e.statusCode} ok` : `${e.statusCode} failed`}
                               color={e.success ? '#10B981' : '#EF4444'} />
                      </td>
                      <td style={{ ...td, fontSize: 12, color: 'var(--text-muted)' }}>{e.ip || '—'}</td>
                      <td style={{ ...td, textAlign: 'right', fontSize: 12, color: 'var(--text-muted)' }}>{e.durationMs}ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      }

      {/* Pagination */}
      {data.pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
          <Btn size="sm" variant="outline" onClick={() => setPage(1)}          disabled={page === 1}>« First</Btn>
          <Btn size="sm" variant="outline" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹ Prev</Btn>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Page {data.page} of {data.pages}</span>
          <Btn size="sm" variant="outline" onClick={() => setPage(p => p + 1)} disabled={page >= data.pages}>Next ›</Btn>
          <Btn size="sm" variant="outline" onClick={() => setPage(data.pages)} disabled={page >= data.pages}>Last »</Btn>
        </div>
      )}

      {/* Detail */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title="Activity Detail" width={640}>
        {detail && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <Badge label={detail.category} color={catColor(detail.category)} />
              <Badge label={detail.action} color="#64748b" />
              <Badge label={detail.success ? 'success' : 'failure'} color={detail.success ? '#10B981' : '#EF4444'} />
            </div>
            <p style={{ fontSize: 15, color: 'var(--text)', marginBottom: 18 }}>{detail.description}</p>
            <DRow k="When"        v={`${fmtDateTime(detail.createdAt)} (${fmtAgo(detail.createdAt)})`} />
            <DRow k="User"        v={detail.userName ? `${detail.userName} · ${detail.userEmail}` : (detail.userEmail || 'anonymous')} />
            <DRow k="Role"        v={detail.userRole || '—'} />
            <DRow k="Target"      v={[detail.targetType, detail.targetLabel].filter(Boolean).join(' · ') || '—'} />
            <DRow k="Target ID"   v={detail.targetId || '—'} />
            <DRow k="Request"     v={`${detail.method} ${detail.path}`} />
            <DRow k="Status"      v={`${detail.statusCode} · ${detail.durationMs}ms`} />
            {detail.message && <DRow k="Message" v={detail.message} />}
            <DRow k="IP address"  v={detail.ip || '—'} />
            <DRow k="User agent"  v={detail.userAgent || '—'} />
            <DRow k="Log ID"      v={detail._id} />
            {detail.meta && (
              <pre style={{
                marginTop: 14, background: 'var(--bg)', border: '1px solid var(--border)',
                borderRadius: 8, padding: 12, fontSize: 12, overflowX: 'auto', color: 'var(--text-muted)',
              }}>{JSON.stringify(detail.meta, null, 2)}</pre>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <Btn variant="outline" onClick={() => { setFilters({ ...EMPTY_FILTERS, user: detail.user || '' }); setPage(1); setDetail(null); }}
                   disabled={!detail.user}>
                Show all by this user
              </Btn>
              <Btn variant="ghost" onClick={() => setDetail(null)}>Close</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* Purge */}
      <Modal open={purgeOpen} onClose={() => setPurgeOpen(false)} title="Purge Activity Log" width={460}>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>
          Delete old entries to keep the log manageable. This cannot be undone.
        </p>
        <Select label="Delete entries older than" value={purgeDays} onChange={e => setPurgeDays(e.target.value)}>
          <option value="365">1 year</option>
          <option value="180">6 months</option>
          <option value="90">90 days</option>
          <option value="30">30 days</option>
          <option value="7">7 days</option>
          <option value="0">Everything (wipe the log)</option>
        </Select>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <Btn variant="ghost" onClick={() => setPurgeOpen(false)}>Cancel</Btn>
          <Btn variant="danger" onClick={purge} disabled={busy}>{busy ? 'Deleting…' : 'Delete'}</Btn>
        </div>
      </Modal>
    </div>
  );
}

const cardH = { fontSize: 14, fontWeight: 600, color: 'var(--heading)', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--border-light)' };
const th = { padding: '12px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' };
const td = { padding: '11px 16px', fontSize: 14, color: 'var(--text)', verticalAlign: 'middle' };

function DRow({ k, v }) {
  return (
    <div style={{ display: 'flex', gap: 12, fontSize: 13, padding: '7px 0', borderBottom: '1px solid var(--border-light)' }}>
      <span style={{ color: 'var(--text-muted)', minWidth: 110 }}>{k}</span>
      <span style={{ color: 'var(--text)', wordBreak: 'break-all' }}>{v}</span>
    </div>
  );
}
